import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CodeAvailability, type CodeAvailabilityStatus } from './CodeAvailability';
import { CodeList } from './CodeList';
import { CodeUploadPanel } from './CodeUploadPanel';
import { PageActions } from '../../app/shell/PageActions';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { codesApi, type CodeAvailabilityRow } from '../../lib/api/codes';
import styles from './CodeScreen.module.css';

/**
 * The dispensary code pool screen (M-05, task 7) — `/dispensary-codes`,
 * reachable only for a session holding `code.manage` (`App.tsx`'s own
 * `rendersCodes`, `navigation.ts`'s own `dispensaryCodes` entry): every one
 * of the five `/dispensary-codes*` endpoints, including the two reads,
 * needs that ability (`codes.ts`'s own docblock, decision C-8), so unlike
 * `DeviceScreen` this component takes no `permissions` prop and gates
 * nothing internally — reaching this screen at all already proves the
 * ability is held.
 *
 * ## Two tabs, the `DeviceScreen.tsx` shape exactly
 *
 * `pool` (filters, table, add form) stays MOUNTED under a `hidden` div while
 * `upload` is active — never unmounted, the identical reasoning
 * `DeviceScreen.tsx`'s own docblock gives for `SensorList`/`ReaderList`: an
 * agent's filters and page position must survive a trip to the upload tab
 * and back. `upload` itself is rendered by a plain ternary (unmounted when
 * not selected) — `CodeUploadPanel` holds no state worth preserving across
 * a switch away, the same choice `DeviceScreen.tsx` makes for its own
 * Import tab.
 *
 * ## The availability summary lives ABOVE the tabs, not inside either one
 *
 * A create (in the pool tab) and an upload (in the upload tab) both change
 * how many free codes exist, so the summary is refetched after either one
 * settles (`loadAvailability`, handed down as `onChanged`/`onUploaded`) and
 * rendered once, above both tabs — never duplicated per tab, and never
 * reset by switching between them.
 */
export function CodeScreen() {
  const { t } = useTranslation();

  const [tab, setTab] = useState<'pool' | 'upload'>('pool');
  const [availabilityRows, setAvailabilityRows] = useState<readonly CodeAvailabilityRow[]>([]);
  const [availabilityStatus, setAvailabilityStatus] = useState<CodeAvailabilityStatus>('loading');

  const loadAvailability = useCallback(async () => {
    setAvailabilityStatus('loading');
    try {
      const rows = await codesApi.availability();
      setAvailabilityRows(rows);
      setAvailabilityStatus('ready');
    } catch {
      setAvailabilityStatus('error');
    }
  }, []);

  useEffect(() => {
    void loadAvailability();
  }, [loadAvailability]);

  const options = [
    { value: 'pool', label: t('codes.tabPool') },
    { value: 'upload', label: t('codes.tabUpload') },
  ];

  return (
    <div className={styles['page']}>
      {/* The pool/upload switch rides in the shell's title row rather than
          spending a row of its own on the page ground. */}
      <PageActions>
        <SegmentedControl
          label={t('codes.tabsLabel')}
          labelPlacement="inline"
          options={options}
          value={tab}
          onChange={(value) => {
            setTab(value as 'pool' | 'upload');
          }}
        />
      </PageActions>

      <CodeAvailability rows={availabilityRows} status={availabilityStatus} />

      {/* `hidden`, never a ternary that unmounts — see this file's own
          docblock. */}
      <div hidden={tab !== 'pool'}>
        <CodeList
          active={tab === 'pool'}
          onChanged={() => {
            void loadAvailability();
          }}
        />
      </div>

      {tab === 'upload' ? (
        <CodeUploadPanel
          onUploaded={() => {
            void loadAvailability();
          }}
        />
      ) : null}
    </div>
  );
}
